import { useCallback, useEffect, useState } from "react";
import { ApiError } from "@/lib/api";
import type { PlatformMeta } from "@/lib/platform-api";

type UsePlatformListOptions<T> = {
  fetcher: (params: { q: string; filter: string; page: number }) => Promise<{ rows: T[]; meta: PlatformMeta }>;
};

export function usePlatformList<T>({ fetcher }: UsePlatformListOptions<T>) {
  const [inputQ, setInputQ] = useState("");
  const [queryQ, setQueryQ] = useState("");
  const [filter, setFilter] = useState("");
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState<T[]>([]);
  const [meta, setMeta] = useState<PlatformMeta | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setQueryQ(inputQ.trim());
      setPage(1);
    }, 300);
    return () => window.clearTimeout(timer);
  }, [inputQ]);

  const submitSearch = useCallback(() => {
    setQueryQ(inputQ.trim());
    setPage(1);
  }, [inputQ]);

  const changeFilter = useCallback((value: string) => {
    setFilter(value);
    setPage(1);
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      setError(null);
      try {
        const result = await fetcher({ q: queryQ, filter, page });
        if (cancelled) return;
        setRows(result.rows);
        setMeta(result.meta);
      } catch (err) {
        if (cancelled) return;
        setRows([]);
        setMeta(null);
        setError(err instanceof ApiError ? err.message : "加载失败，请稍后重试");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, [fetcher, queryQ, filter, page]);

  return {
    inputQ,
    setInputQ,
    filter,
    setFilter: changeFilter,
    page,
    setPage,
    rows,
    meta,
    loading,
    error,
    submitSearch,
  };
}
